import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle, RotateCcw, AlertTriangle, Loader2 } from "lucide-react";
import commandxLogo from "@/assets/commandx-logo.png";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
// Public approval link — does NOT share auth storage with the main app client.
// Using a unique storageKey + no persistence prevents the "Multiple GoTrueClient
// instances" warning and avoids racing the main client's INITIAL_SESSION event.
const anonClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: "sb-public-approval-anon",
  },
});

const fmt = (n: number | null | undefined) =>
  n != null ? `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

const safeDate = (d: string | null | undefined) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};

type PageState = "loading" | "view" | "approved" | "rejected" | "changes_requested" | "already_approved" | "already_rejected" | "expired" | "invalid";

interface EstimateData {
  id: string;
  estimate_number: string;
  customer_name: string | null;
  customer_contact_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  txn_date: string;
  expiry_date: string | null;
  status: string;
  line_items: any[];
  subtotal: number;
  total_amount: number;
  tax_rate: number;
  tax_amount: number;
  notes: string | null;
  share_token_expires_at: string;
  approved_at: string | null;
  approved_by_name: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  company_name: string | null;
  company_address: string | null;
  company_city_state_zip: string | null;
  company_phone: string | null;
  company_website: string | null;
  company_logo_url: string | null;
  estimator_name: string | null;
  project_name: string | null;
  project_address: string | null;
  estimate_groups: any[] | null;
  print_settings: any | null;
}

// Simple signature canvas
const SignaturePad = ({ onSignature }: { onSignature: (data: string) => void }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    if ("touches" in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setDrawing(true);
    setHasDrawn(true);
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const pos = getPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    setDrawing(false);
    if (hasDrawn && canvasRef.current) {
      onSignature(canvasRef.current.toDataURL());
    }
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onSignature("");
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Draw your signature above</p>
        <Button variant="ghost" size="sm" onClick={clear} type="button">Clear</Button>
      </div>
    </div>
  );
};

/* ─── Inline styles for PDF-like document ─── */
const docStyles = {
  page: {
    maxWidth: "8.5in",
    margin: "0 auto",
    fontFamily: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#111",
    background: "#fff",
    padding: "40px 48px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
    borderRadius: "4px",
  } as React.CSSProperties,
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "3px solid #1e3a5f",
    paddingBottom: "16px",
    marginBottom: "20px",
  } as React.CSSProperties,
  companyName: {
    fontSize: "22px",
    fontWeight: 800,
    color: "#1e3a5f",
    margin: 0,
    letterSpacing: "-0.01em",
  } as React.CSSProperties,
  companyDetail: {
    fontSize: "11px",
    color: "#555",
    margin: "2px 0",
  } as React.CSSProperties,
  estimateTitle: {
    fontSize: "28px",
    fontWeight: 800,
    color: "#1e3a5f",
    margin: 0,
    letterSpacing: "0.04em",
  } as React.CSSProperties,
  metaLabel: {
    fontSize: "11px",
    color: "#666",
    textAlign: "right" as const,
  } as React.CSSProperties,
  metaValue: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#111",
    textAlign: "right" as const,
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1e3a5f",
    borderBottom: "2px solid #1e3a5f",
    paddingBottom: "4px",
    margin: "24px 0 10px",
  } as React.CSSProperties,
  th: {
    textAlign: "left" as const,
    padding: "8px 6px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#666",
    borderBottom: "2px solid #ddd",
  } as React.CSSProperties,
  td: {
    padding: "10px 6px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top" as const,
  } as React.CSSProperties,
};

const EstimateApproval = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>("loading");
  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [signature, setSignature] = useState("");
  const [action, setAction] = useState<"approve" | "reject" | "changes" | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    loadEstimate();
  }, [token]);

  const loadEstimate = async () => {
    const { data, error } = await anonClient.rpc("get_estimate_by_token", { p_token: token! });
    if (error || !data) { setState("invalid"); return; }
    const res = data as any;
    if (res.expired) { setState("expired"); return; }
    const est = res as unknown as EstimateData;
    setEstimate(est);
    if (est.status === "Accepted") setState("already_approved");
    else if (est.status === "Rejected") setState("already_rejected");
    else if (est.status === "Sent") setState("view");
    else setState("invalid");
  };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      if (action === "approve") {
        const { data } = await anonClient.rpc("approve_estimate_by_token", {
          p_token: token!, p_name: name, p_email: email, p_signature: signature || null, p_notes: notes || null,
        });
        const res = data as any;
        if (res?.success) setState("approved");
        else throw new Error(res?.error || "Failed");
      } else if (action === "reject") {
        if (!notes.trim()) return;
        const { data } = await anonClient.rpc("reject_estimate_by_token", {
          p_token: token!, p_name: name, p_email: email, p_reason: notes,
        });
        const res = data as any;
        if (res?.success) setState("rejected");
        else throw new Error(res?.error || "Failed");
      } else if (action === "changes") {
        if (!notes.trim()) return;
        const { data } = await anonClient.rpc("request_changes_by_token", {
          p_token: token!, p_name: name, p_email: email, p_notes: notes,
        });
        const res = data as any;
        if (res?.success) setState("changes_requested");
        else throw new Error(res?.error || "Failed");
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Result pages
  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Invalid Link</h2>
            <p className="text-muted-foreground">This link is invalid or has been revoked.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "expired") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto" />
            <h2 className="text-xl font-semibold">Estimate Expired</h2>
            <p className="text-muted-foreground">This estimate has expired. Please contact us for an updated estimate.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "approved") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-semibold">Estimate Approved!</h2>
            <p className="text-muted-foreground">We've been notified and will begin project setup.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Estimate Declined</h2>
            <p className="text-muted-foreground">We've received your response. Thank you for your consideration.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "changes_requested") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <RotateCcw className="h-12 w-12 text-blue-500 mx-auto" />
            <h2 className="text-xl font-semibold">Changes Requested</h2>
            <p className="text-muted-foreground">Your feedback has been sent. We'll follow up shortly.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "already_approved") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-semibold">Already Approved</h2>
            <p className="text-muted-foreground">
              This estimate was approved on {safeDate(estimate?.approved_at)} by {estimate?.approved_by_name}.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === "already_rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center space-y-3">
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold">Already Declined</h2>
            <p className="text-muted-foreground">This estimate was declined on {safeDate(estimate?.rejected_at)}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main estimate view
  if (!estimate) return null;
  const lineItems = Array.isArray(estimate.line_items) ? estimate.line_items : [];

  const companyAddr = [estimate.company_address, estimate.company_city_state_zip].filter(Boolean).join("\n");

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div style={{ maxWidth: "8.5in", margin: "0 auto" }} className="space-y-6">
        {/* ─── PDF-like Document ─── */}
        <div style={docStyles.page}>
          {/* Header */}
          <div style={docStyles.headerRow}>
            <div style={{ maxWidth: "55%" }}>
              {estimate.company_logo_url && (
                <img src={estimate.company_logo_url} alt={estimate.company_name ?? ""} style={{ maxHeight: "48px", objectFit: "contain", marginBottom: "8px" }} />
              )}
              <p style={docStyles.companyName}>{estimate.company_name || "Company"}</p>
              {estimate.company_address && <p style={docStyles.companyDetail}>{estimate.company_address}</p>}
              {estimate.company_city_state_zip && <p style={docStyles.companyDetail}>{estimate.company_city_state_zip}</p>}
              {estimate.company_website && (
                <p style={{ ...docStyles.companyDetail, color: "#1e3a5f" }}>{estimate.company_website}</p>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={docStyles.estimateTitle}>ESTIMATE</p>
              <table style={{ marginLeft: "auto", borderCollapse: "collapse", marginTop: "6px" }}>
                <tbody>
                  <tr>
                    <td style={docStyles.metaLabel}>Estimate #:&nbsp;&nbsp;</td>
                    <td style={docStyles.metaValue}>EST-{estimate.estimate_number}</td>
                  </tr>
                  <tr>
                    <td style={docStyles.metaLabel}>Date:&nbsp;&nbsp;</td>
                    <td style={docStyles.metaValue}>{safeDate(estimate.txn_date)}</td>
                  </tr>
                  {estimate.estimator_name && (
                    <tr>
                      <td style={{ ...docStyles.metaLabel, fontSize: "10px", color: "#888" }}>
                        {estimate.company_name} · <span style={{ fontStyle: "italic" }}>rfame.qbm</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Customer / Project Info */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "28px", marginBottom: "8px", paddingBottom: "16px", borderBottom: "1px solid #ddd" }}>
            <div>
              <div style={{ marginBottom: "6px" }}>
                <span style={{ fontSize: "11px", color: "#666" }}>Customer: </span>
                <span style={{ fontSize: "12px", fontWeight: 700 }}>{estimate.customer_name || "—"}</span>
              </div>
              {estimate.customer_contact_name && (
                <div style={{ marginBottom: "4px" }}>
                  <span style={{ fontSize: "11px", color: "#666" }}>Attn: </span>
                  <span style={{ fontSize: "11px", fontWeight: 600 }}>{estimate.customer_contact_name}</span>
                </div>
              )}
              {(estimate.customer_phone || estimate.customer_email) && (
                <div style={{ marginBottom: "4px", fontSize: "11px", color: "#555" }}>
                  {[estimate.customer_phone, estimate.customer_email].filter(Boolean).join(" · ")}
                </div>
              )}
              {(estimate.project_name || estimate.project_address) && (
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#666" }}>Project: </span>
                  <span style={{ fontSize: "12px", fontWeight: 600 }}>
                    {estimate.project_name || ""}
                    {estimate.project_address && (
                      <span style={{ fontWeight: 400, display: "block", paddingLeft: "48px", fontSize: "11px", color: "#555" }}>
                        {estimate.project_address}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {estimate.estimator_name && (
                <div>
                  <span style={{ fontSize: "11px", color: "#666" }}>Project Manager: </span>
                  <span style={{ fontSize: "12px", fontWeight: 600 }}>{estimate.estimator_name}</span>
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <table style={{ marginLeft: "auto", borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={docStyles.metaLabel}>Estimate #:&nbsp;&nbsp;</td>
                    <td style={docStyles.metaValue}>EST-{estimate.estimate_number}</td>
                  </tr>
                  <tr>
                    <td style={docStyles.metaLabel}>Date:&nbsp;&nbsp;</td>
                    <td style={docStyles.metaValue}>{safeDate(estimate.txn_date)}</td>
                  </tr>
                  {estimate.estimator_name && (
                    <tr>
                      <td style={docStyles.metaLabel}>Estimator:&nbsp;&nbsp;</td>
                      <td style={docStyles.metaValue}>{estimate.estimator_name}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {companyAddr && (
                <p style={{ fontSize: "10px", color: "#888", marginTop: "6px", whiteSpace: "pre-line" }}>{companyAddr}</p>
              )}
              {estimate.company_website && (
                <p style={{ fontSize: "10px", color: "#888" }}>{estimate.company_website}</p>
              )}
            </div>
          </div>

          {/* Scope of Work */}
          <h2 style={docStyles.sectionTitle}>Scope of Work</h2>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...docStyles.th, width: "30px" }}>#</th>
                <th style={docStyles.th}>Description</th>
                <th style={{ ...docStyles.th, width: "70px", textAlign: "right" }}>Qty</th>
                <th style={{ ...docStyles.th, width: "50px", textAlign: "center" }}>Unit</th>
                <th style={{ ...docStyles.th, width: "100px", textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item: any, idx: number) => {
                const desc = item.customer_description || item.description || `Line ${idx + 1}`;
                const qty = item.qty ?? 0;
                const unit = item.unit || "";
                const totalBilled = item.total_billed ?? item.total ?? 0;
                const subLines = Array.isArray(item.sub_lines) ? item.sub_lines : [];

                return (
                  <React.Fragment key={idx}>
                    <tr>
                      <td style={{ ...docStyles.td, color: "#888", fontFamily: "monospace", fontSize: "11px" }}>{idx + 1}</td>
                      <td style={{ ...docStyles.td, fontWeight: 600 }}>{desc}</td>
                      <td style={{ ...docStyles.td, textAlign: "right", fontFamily: "monospace" }}>
                        {Number(qty).toLocaleString()}
                      </td>
                      <td style={{ ...docStyles.td, textAlign: "center", color: "#666" }}>{unit}</td>
                      <td style={{ ...docStyles.td, textAlign: "right", fontWeight: 700, fontFamily: "monospace" }}>
                        {fmt(totalBilled)}
                      </td>
                    </tr>
                    {subLines.map((sl: any, si: number) => (
                      <tr key={si}>
                        <td style={{ padding: "2px 6px", borderBottom: si === subLines.length - 1 ? "1px solid #eee" : "none" }} />
                        <td
                          colSpan={3}
                          style={{
                            padding: "3px 6px 3px 28px",
                            fontSize: "11px",
                            color: "#666",
                            borderBottom: si === subLines.length - 1 ? "1px solid #eee" : "none",
                          }}
                        >
                          • <span style={{
                            fontWeight: 600,
                            color: sl.type === "labor" ? "#1d4ed8" : "#92400e",
                          }}>
                            {sl.type === "labor" ? "Labor" : "Material"}:
                          </span>{" "}
                          {sl.description} <span style={{ fontStyle: "italic", color: "#999" }}>(Included)</span>
                        </td>
                        <td style={{ borderBottom: si === subLines.length - 1 ? "1px solid #eee" : "none" }} />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{ marginTop: "24px", borderTop: "2px solid #1e3a5f", paddingTop: "12px" }}>
            <table style={{ marginLeft: "auto", borderCollapse: "collapse", minWidth: "240px" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "4px 16px 4px 0", fontSize: "12px", color: "#555", fontWeight: 600 }}>Subtotal:</td>
                  <td style={{ padding: "4px 0", fontSize: "12px", fontWeight: 600, textAlign: "right", fontFamily: "monospace" }}>{fmt(estimate.subtotal)}</td>
                </tr>
                {estimate.tax_amount > 0 && (
                  <tr>
                    <td style={{ padding: "4px 16px 4px 0", fontSize: "12px", color: "#555" }}>Tax ({estimate.tax_rate ?? 0}%):</td>
                    <td style={{ padding: "4px 0", fontSize: "12px", textAlign: "right", fontFamily: "monospace" }}>{fmt(estimate.tax_amount)}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: "8px 16px 4px 0", fontSize: "14px", fontWeight: 800, borderTop: "2px solid #111" }}>Total:</td>
                  <td style={{ padding: "8px 0 4px", fontSize: "14px", fontWeight: 800, textAlign: "right", fontFamily: "monospace", borderTop: "2px solid #111" }}>{fmt(estimate.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {estimate.notes && (
            <div style={{ marginTop: "24px" }}>
              <h3 style={{ fontSize: "12px", fontWeight: 700, color: "#1e3a5f", borderBottom: "1px solid #ddd", paddingBottom: "4px", marginBottom: "6px" }}>Notes</h3>
              <p style={{ fontSize: "11px", color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{estimate.notes}</p>
            </div>
          )}
        </div>

        {/* ─── Response Section (interactive, outside PDF look) ─── */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Your Response</h3>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium">Your Name *</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
              </div>
              <div>
                <label className="text-sm font-medium">Your Email *</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@company.com" />
              </div>
            </div>

            {action === "approve" && (
              <div className="space-y-3 border rounded-lg p-4 bg-emerald-50/50">
                <p className="text-sm font-medium">By signing, I approve this estimate and authorize the contractor to proceed.</p>
                <SignaturePad onSignature={setSignature} />
                <div>
                  <label className="text-sm font-medium">Notes (optional)</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional notes..." rows={2} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim()} className="flex-1">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Confirm Approval
                  </Button>
                  <Button variant="outline" onClick={() => setAction(null)}>Back</Button>
                </div>
              </div>
            )}

            {action === "reject" && (
              <div className="space-y-3 border rounded-lg p-4 bg-red-50/50">
                <div>
                  <label className="text-sm font-medium">Please let us know why you're declining *</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for declining..." rows={3} />
                </div>
                <div className="flex gap-2">
                  <Button variant="destructive" onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim() || !notes.trim()} className="flex-1">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                    Confirm Decline
                  </Button>
                  <Button variant="outline" onClick={() => setAction(null)}>Back</Button>
                </div>
              </div>
            )}

            {action === "changes" && (
              <div className="space-y-3 border rounded-lg p-4 bg-blue-50/50">
                <div>
                  <label className="text-sm font-medium">Please describe the changes needed *</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changes would you like?" rows={3} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSubmit} disabled={submitting || !name.trim() || !email.trim() || !notes.trim()} className="flex-1" variant="secondary">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}
                    Submit Feedback
                  </Button>
                  <Button variant="outline" onClick={() => setAction(null)}>Back</Button>
                </div>
              </div>
            )}

            {!action && (
              <div className="grid gap-2 sm:grid-cols-3">
                <Button onClick={() => setAction("approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve Estimate
                </Button>
                <Button variant="outline" onClick={() => setAction("changes")}>
                  <RotateCcw className="h-4 w-4 mr-1" /> Request Changes
                </Button>
                <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setAction("reject")}>
                  <XCircle className="h-4 w-4 mr-1" /> Decline
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-center gap-1.5">
          <img src={commandxLogo} alt="CommandX" className="h-4 object-contain" />
          <span className="text-xs text-muted-foreground">Powered by CommandX Ops Center</span>
        </div>
      </div>
    </div>
  );
};

export default EstimateApproval;
